import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../lib/supabaseClient";
import * as Select from "@radix-ui/react-select";
import { ChevronDownIcon } from "lucide-react";
import { useProjectContext } from "../../../contexts/ProjectContext";

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [clubs, setClubs] = useState<any[]>([]);
  const [selectedClub, setSelectedClub] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendSuccess, setResendSuccess] = useState(false);

  const resendConfirmationEmail = async () => {
    setLoading(true);
    setError("");
    setResendSuccess(false);

    const { error: resendError } = await supabase.auth.signUp({
      email,
      password,
    });

    if (resendError) {
      setError(resendError.message);
    } else {
      setResendSuccess(true);
    }

    setLoading(false);
  };

  const { project } = useProjectContext();

  useEffect(() => {
    if (!project) return;

    const fetchClubs = async () => {
      const { data, error } = await supabase
        .from("clubs")
        .select("id, name")
        .eq("project_id", project.id)
        .order("name");

      if (!error) setClubs(data);
    };

    fetchClubs();
  }, [project]);

  if (!project || !Array.isArray(clubs)) {
    return (
      <div className="max-w-md mx-auto mt-10 p-6 bg-white border rounded shadow">
        <p className="text-center">Loading...</p>
      </div>
    );
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!selectedClub) {
      setError("Please select a club.");
      return;
    }

    setLoading(true);

    // Check if email already exists in users table
    const { data: existingUser, error: checkError } = await supabase
      .from("users")
      .select("id")
      .eq("email", email)
      .single();

    if (existingUser) {
      setError("An account with this email already exists.");
      setLoading(false);
      return;
    }

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
    });

    if (signUpError || !signUpData.user) {
      if (
        signUpError?.message === "User already registered" ||
        (typeof signUpError?.message === "string" && signUpError?.message.includes("duplicate key value"))
      ) {
        setError("User already registered.");
        setResendSuccess(false);
      } else {
        setError(signUpError?.message || "Signup failed.");
      }
      setLoading(false);
      return;
    }

    const { error: insertError } = await supabase.from("users").insert({
      id: signUpData.user.id,
      email,
      name,
      role: "captain",
      club_id: selectedClub,
      project_id: project?.id,
    });

    if (insertError) {
      setError(insertError.message);
    } else {
      navigate("/login");
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col justify-between">
      <div className="max-w-md mx-auto mt-10 p-6 bg-white border rounded shadow">
        <div className="text-center mb-4">
          <img
            src="/lovable-uploads/d4c7df19-9c74-4ee3-8b7d-3585fbf47e66.png"
            alt="Gwent Golf Union Logo"
            className="mx-auto mb-2 w-32"
          />
          <h1 className="text-2xl">Gwent Men's Inter-Club Team Golf</h1>
          <p className="text-lg text-gray-700">Captains Sign-Up</p>
        </div>
        <form onSubmit={handleSignup} className="space-y-4">
          <input
            type="text"
            className="w-full p-2 border rounded"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <input
            type="email"
            className="w-full p-2 border rounded"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            className="w-full p-2 border rounded"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <input
            type="password"
            className="w-full p-2 border rounded"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <div className="relative">
            <Select.Root value={selectedClub} onValueChange={setSelectedClub}>
              <Select.Trigger
                className="w-full border rounded-md p-2 flex justify-between items-center bg-white text-left shadow-sm transition-colors focus:ring-2 focus:ring-[#30702F] focus:border-[#30702F] outline-none"
                aria-label="Club"
              >
                <Select.Value placeholder="Select your club" />
                <Select.Icon>
                  <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                </Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                <Select.Content
                  position="popper"
                  className="w-[--radix-select-trigger-width] bg-white border border-gray-200 rounded-md shadow-lg overflow-auto max-h-60 z-50"
                >
                  <Select.Viewport>
                    {clubs.map((club) => (
                      <Select.Item
                        key={club.id}
                        value={club.id}
                        className="px-3 py-2 cursor-pointer hover:bg-[#f1f5f9] data-[state=checked]:bg-[#30702F] data-[state=checked]:text-white data-[highlighted]:bg-[#e5e7eb] transition-colors"
                      >
                        <Select.ItemText>{club.name}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Viewport>
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>
          {error && <div className="text-red-600">{error}</div>}
          {error === "User already registered." && !resendSuccess && (
            <div className="text-sm text-gray-700 mt-2">
              Didn't get the confirmation email?{" "}
              <button
                type="button"
                onClick={resendConfirmationEmail}
                className="text-[#30702F] underline hover:opacity-80"
              >
                Resend confirmation email
              </button>
            </div>
          )}
          {resendSuccess && (
            <div className="text-green-600">Confirmation email resent. Please check your inbox.</div>
          )}
          {!resendSuccess && error === "Signup failed." && (
            <div className="text-sm text-gray-700 mt-2">
              Didn't get the email?{" "}
              <button
                type="button"
                onClick={resendConfirmationEmail}
                className="text-[#30702F] underline hover:opacity-80"
              >
                Resend confirmation email
              </button>
            </div>
          )}
          <button
            type="submit"
            className="w-full p-2 bg-[#30702F] text-white rounded disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Signing up..." : "Sign Up"}
          </button>
          <p className="text-sm text-gray-600 text-center mt-2">
            <em>Once you have signed up, please check your emails to verify your email address before logging in</em>
          </p>
        </form>
      </div>
      <footer className="mt-6 flex justify-center items-center gap-2 text-xs text-gray-500">
        <p className="m-0">Provided by</p>
        <img
          src="/OfficialScoringMainLogo.jpeg"
          alt="Official Scoring Logo"
          className="w-20"
        />
      </footer>
    </div>
  );
};

export default Signup;
